import { ParsedCSVData, ValidationError } from '@/types/upload';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * お知らせ（インフォメーション）CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に、Book / Coupon 等と同様に全ルールを
 * クライアントで検証してプレビュー可否を判定する。サーバー（ProcessUpload Lambda /
 * InformationUploadService）でも同一ルールを再検証する（多層防御）。
 *
 * Coupon と同じ **1 行 = 1 お知らせ**のフラット形式（6 列）で、data_type による階層を持たない。
 *
 * 仕様の正本: docs/features/information.md「CSV フォーマット」「CSV バリデーションルール」（V01〜V12）
 * 「本文 HTML 仕様」（V10 / V11 の許可タグ・許可属性）。
 *
 * 権威はサーバー（information_service.py）側にあり、ここはアップロード前の UX 向け事前検証。
 * 日時 / platform / HTML 許可タグ等のルールはバックエンドと手動ミラーのため、
 * 仕様変更時は docs/features/information.md を基点に information_service.py と本ファイルの
 * 両方を更新すること（エラーメッセージも同一文言を維持する）。
 */

const COLUMNS = [
  'information_name',
  'title',
  'start_at',
  'end_at',
  'platform',
  'body',
] as const;

// 列名をキーに持つ 1 行分の型（値は常に string）。詳細は utils/csvRow.ts。
type InformationColumn = (typeof COLUMNS)[number];
type InformationRow = CsvRow<typeof COLUMNS>;

// 必須列（V03。end_at のみ任意 = 空欄は無期限）
const REQUIRED_COLUMNS: readonly InformationColumn[] = [
  'information_name',
  'title',
  'start_at',
  'platform',
  'body',
];

// information_name の書式（V04）。旧環境の値が数字始まりのため AlphanumericSlug は使わない
// （schemas の InformationName と同一パターン）。
const INFORMATION_NAME_RE = /^[A-Za-z0-9_]{1,64}$/;
const PLATFORM_RE = /^[01]{3}$/; // V08
// CSV 日時（`YYYY/M/D H:MM`・JST）。commons の CsvDateTime（codegen）と同じ範囲を受理する:
// 年月日と時は `/` `-` 区切り・ゼロパディング省略可、秒は省略可だが分と秒は 2 桁固定。
// ⚠ information_service.py の _CSV_DATETIME_PATTERN と完全に一致させること。
const CSV_DATETIME_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2}) (\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const DATETIME_FORMAT_HINT = '（YYYY/M/D H:MM）';

const TITLE_MAX_LENGTH = 100; // V09
const BODY_MAX_LENGTH = 2000; // V09
export const MAX_DATA_ROWS = 500; // V12

// 本文 HTML の許可タグ・許可属性（V10 / V11。docs/features/information.md「本文 HTML 仕様」）。
// a.href / img.src は `/` 始まりの相対パス または `https://` 始まりのみ。`//example.com`（プロトコル相対 URL）は
// 外部遷移になるため相対パスとして扱わない（`/(?!/)` で除外。information_service.py と同一）。
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'p',
  'br',
  'a',
  'strong',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'img',
  'div',
]);
const SAFE_URL_RE = /^(\/(?!\/)|https:\/\/)/;
const ALIGN_VALUES: ReadonlySet<string> = new Set(['left', 'center', 'right']);
const IMG_WIDTH_RE = /^\d+%?$/;

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

// 存在しない日付（例: 2026-02-30）を UTC ロールオーバーの有無で弾く
const isRealDate = (year: number, month: number, day: number): boolean => {
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
};

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/** CSV 日時を分解する。書式不正・時刻範囲外・存在しない日付は null（couponCsvValidation と同方式）。 */
const parseDateTime = (value: string): DateTimeParts | null => {
  const matched = CSV_DATETIME_RE.exec(value.trim());
  if (!matched) return null;
  const [, y, mo, d, h, mi, sec] = matched;
  const parts: DateTimeParts = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
    second: Number(sec ?? 0),
  };
  if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) return null;
  if (!isRealDate(parts.year, parts.month, parts.day)) return null;
  return parts;
};

// CSV のタイムゾーン無し日時は JST（UTC+9）とみなす（サーバ DateTimeUtils と一致）。
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const toEpoch = (p: DateTimeParts): number =>
  Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - JST_OFFSET_MS;

/**
 * 本文 HTML を許可タグ・許可属性のホワイトリストで検証する（V10 / V11）。
 * エラーメッセージは information_service.py の _BodyHtmlValidator と同一文言を維持すること。
 */
export const validateBodyHtml = (body: string): string[] => {
  const errors: string[] = [];
  const doc = new DOMParser().parseFromString(body, 'text/html');

  doc.body.querySelectorAll('*').forEach((el) => {
    const tag = el.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      errors.push(`使用できないタグが含まれています: ${tag}`);
      return;
    }
    if (tag === 'img' && !el.hasAttribute('src')) {
      errors.push('画像の参照先は/またはhttps://で始まる必要があります');
    }
    Array.from(el.attributes).forEach((attr) => {
      if (tag === 'a' && attr.name === 'href') {
        if (!SAFE_URL_RE.test(attr.value)) {
          errors.push('リンク先は/またはhttps://で始まる必要があります');
        }
      } else if (tag === 'a' && attr.name === 'target') {
        if (attr.value !== '_blank') {
          errors.push(`target に指定できるのは "_blank" のみです: ${attr.value}`);
        }
      } else if (tag === 'img' && attr.name === 'src') {
        if (!SAFE_URL_RE.test(attr.value)) {
          errors.push('画像の参照先は/またはhttps://で始まる必要があります');
        }
      } else if (tag === 'img' && attr.name === 'alt') {
        // alt は任意文字列を許可
      } else if (tag === 'img' && attr.name === 'width') {
        if (!IMG_WIDTH_RE.test(attr.value)) {
          errors.push('画像の width は数字または数字+% で指定してください');
        }
      } else if (tag === 'div' && attr.name === 'align') {
        if (!ALIGN_VALUES.has(attr.value)) {
          errors.push(`align に指定できるのは left / center / right のみです: ${attr.value}`);
        }
      } else {
        errors.push(`使用できない属性が含まれています: ${tag} の ${attr.name}`);
      }
    });
  });

  // DOMParser は <head> 側へ移動するタグ（style / title 等）があるため head 配下も検証する
  doc.head.querySelectorAll('*').forEach((el) => {
    errors.push(`使用できないタグが含まれています: ${el.tagName.toLowerCase()}`);
  });

  // DOMParser は対応する開始タグを持たない閉じタグ（例: <p>x</span>）を HTML5 パース規則で
  // 無視して DOM に残さないため、サーバ（HTMLParser の handle_endtag はスタック対応に関係なく
  // 発火する）と判定を揃える目的で閉じタグ名を直接走査する（MR !570 Duo 指摘）。
  body.replace(/<\/\s*([a-zA-Z][a-zA-Z0-9]*)/g, (whole: string, tag: string) => {
    if (!ALLOWED_TAGS.has(tag.toLowerCase())) {
      errors.push(`使用できないタグが含まれています: ${tag.toLowerCase()}`);
    }
    return whole;
  });

  // コメントはタグ検証をすり抜ける埋め込み経路になり得るため一律エラー（サーバと同一ルール）
  const iterator = doc.createNodeIterator(doc, NodeFilter.SHOW_COMMENT);
  if (iterator.nextNode()) {
    errors.push('HTML コメントは使用できません');
  }

  return [...new Set(errors)];
};

const validateRow = (rowNum: number, d: InformationRow, errors: ValidationError[]): void => {
  // V03: 必須列
  REQUIRED_COLUMNS.forEach((col) => {
    if (!d[col]) errors.push(err(rowNum, col, d[col], `${col} は必須です`));
  });

  // V04: information_name の書式
  if (d.information_name && !INFORMATION_NAME_RE.test(d.information_name)) {
    errors.push(
      err(
        rowNum,
        'information_name',
        d.information_name,
        'information_name は半角英数とアンダースコア（_）の 1〜64 桁で指定してください'
      )
    );
  }

  // V09: 文字数上限。サーバ（Python len = コードポイント数）と揃えるため、UTF-16 コード単位の
  // .length ではなくスプレッドでコードポイント数を数える（サロゲートペア絵文字等で差が出ない）
  if ([...d.title].length > TITLE_MAX_LENGTH) {
    errors.push(
      err(rowNum, 'title', d.title, `title は ${TITLE_MAX_LENGTH} 文字以内で入力してください`)
    );
  }
  if ([...d.body].length > BODY_MAX_LENGTH) {
    errors.push(
      err(rowNum, 'body', d.body, `body は ${BODY_MAX_LENGTH} 文字以内で入力してください`)
    );
  }

  // V06: 日時形式（必須チェックは上に集約。値がある時だけ形式検証）
  (['start_at', 'end_at'] as const).forEach((col) => {
    if (d[col] && parseDateTime(d[col]) === null) {
      errors.push(err(rowNum, col, d[col], `${col} の日時形式が不正です${DATETIME_FORMAT_HINT}`));
    }
  });

  // V07: start_at < end_at（両方揃っていて形式が正しいときのみ。同一日時も不可）
  const start = d.start_at ? parseDateTime(d.start_at) : null;
  const end = d.end_at ? parseDateTime(d.end_at) : null;
  if (start && end && toEpoch(start) >= toEpoch(end)) {
    errors.push(err(rowNum, 'end_at', d.end_at, '掲載開始日時が掲載終了日時以降になっています'));
  }

  // V08: platform（3 桁の 0/1・000 不可）
  if (d.platform) {
    if (!PLATFORM_RE.test(d.platform)) {
      errors.push(
        err(
          rowNum,
          'platform',
          d.platform,
          'platform は 3 桁の 0 / 1（web/ios/android の順）で指定してください'
        )
      );
    } else if (d.platform === '000') {
      errors.push(err(rowNum, 'platform', d.platform, 'platform は 000 不可（最低 1 つは表示）'));
    }
  }

  // V10 / V11: 本文 HTML（許可タグ・許可属性のみ）
  if (d.body) {
    validateBodyHtml(d.body).forEach((message) => {
      errors.push(err(rowNum, 'body', d.body, message));
    });
  }
};

export const validateInformationCsv = (data: ParsedCSVData): ValidationError[] => {
  // V01 / V02: ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [
      err(
        1,
        'ヘッダー',
        data.headers.join(','),
        `ヘッダが不正です。期待: ${COLUMNS.join(',')}`
      ),
    ];
  }

  // V01: ヘッダー行（2 行）が存在する。2 行目（日本語定義名行）を削除した CSV は先頭の
  // データ行が定義名行として読み飛ばされ、1 件目だけが黙って欠落するため明示的に弾く
  // （information_service.py と同一ルール・同一文言。定義名行の先頭セルは日本語のため
  // information_name の書式に一致しないことを利用する。ヘッダー 1 行のみの CSV も
  // parseCSV のフォールバックで displayHeaders = headers となり本チェックで検出される）。
  // 先頭セルは slice + join で取り出す（displayHeaders は空にならないため ?? の
  // 到達不能分岐を作らない）。
  if (INFORMATION_NAME_RE.test(data.displayHeaders.slice(0, 1).join(''))) {
    return [
      err(
        2,
        'ヘッダー',
        data.displayHeaders.join(','),
        'ヘッダー行が不足しています（2行必要）'
      ),
    ];
  }

  if (data.rows.length === 0) {
    return [
      err(1, 'データ', '', 'データ行がありません（1=カラム名 / 2=定義名 / 3 行目以降=データ）'),
    ];
  }

  const errors: ValidationError[] = [];

  // V12: データ行の上限
  if (data.rows.length > MAX_DATA_ROWS) {
    errors.push(
      err(
        1,
        'データ',
        String(data.rows.length),
        `データ行が上限（${MAX_DATA_ROWS} 行）を超えています（${data.rows.length} 行）`
      )
    );
  }

  // parseCSV が 2 行目（定義名）をスキップ済みのため data.rows[0] は実ファイル 3 行目に相当する。
  const seenNames = new Map<string, number>();

  data.rows.forEach((row, idx) => {
    const rowNum = idx + 3;

    if (row.length !== COLUMNS.length) {
      errors.push(
        err(
          rowNum,
          '列数',
          row.join(','),
          `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`
        )
      );
      return;
    }

    const d = toCsvRow(COLUMNS, row);

    validateRow(rowNum, d, errors);

    // V05: information_name の重複（後の行が前の行を黙って上書きするためエラーにする）
    if (d.information_name) {
      const prev = seenNames.get(d.information_name);
      if (prev !== undefined) {
        errors.push(
          err(
            rowNum,
            'information_name',
            d.information_name,
            `information_name が重複しています: '${d.information_name}'（${prev} 行目と同一）`
          )
        );
      } else {
        seenNames.set(d.information_name, rowNum);
      }
    }
  });

  return errors;
};
